import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function Register() {
  const [formData, setFormData] = useState({
    username: '',
    student_name: '',  // 學生姓名
    password: '',
    email: '',
    phone: '',
    role: 'student',
    gender: '',
    class_location: '',  // 上堂地點
    student_id_to_link: '' // For parent registration
  });
  const [loading, setLoading] = useState(false);
  const [org, setOrg] = useState(null);
  const [locations, setLocations] = useState([]);  // 地點列表
  const { orgId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const orgData = localStorage.getItem('currentOrg');
    let organizationId = orgId;  // From URL params
    
    if (orgData) {
      const parsedOrg = JSON.parse(orgData);
      setOrg(parsedOrg);
      // If orgId is not in URL, try to get from localStorage
      if (!organizationId && parsedOrg.organization_id) {
        organizationId = parsedOrg.organization_id;
      }
    }
    
    console.log('Fetching locations for org:', organizationId);
    
    // 獲取當前機構的地點列表
    if (organizationId) {
      fetchLocations(organizationId);
    } else {
      console.warn('No organization ID found');
    }
  }, [orgId]);

  const fetchLocations = async (organizationId) => {
    try {
      console.log('Calling API:', `${API}/public/locations/${organizationId}`);
      const response = await axios.get(`${API}/public/locations/${organizationId}`);
      console.log('Locations received:', response.data);
      setLocations(response.data);
      if (response.data.length === 0) {
        toast.info('該機構暫無設置地點，請聯繫管理員');
      }
    } catch (error) {
      console.error('獲取地點失敗:', error);
      toast.error('無法獲取地點列表: ' + (error.response?.data?.detail || error.message));
      // 如果獲取失敗，設置為空數組
      setLocations([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password || !formData.email || !formData.phone) {
      toast.error('請填寫所有欄位');
      return;
    }

    // Check if parent needs to provide student ID
    if (formData.role === 'parent' && !formData.student_id_to_link) {
      toast.error('家長註冊需要填寫學生ID進行綁定');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        ...formData,
        organization_id: orgId
      });
      
      // Show appropriate success message based on role
      const roleLabels = {
        student: '學生',
        parent: '家長',
        coach: '教練'
      };
      
      const roleName = roleLabels[formData.role] || '用戶';
      
      if (res.data.student_id) {
        toast.success(
          `✅ ${roleName}帳號申請成功！\n您的學生ID: ${res.data.student_id}\n\n請等待管理員審批後即可登入使用。`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          `✅ ${roleName}帳號申請成功！\n\n請等待管理員審批通過後，即可登入使用。我們會盡快處理您的申請。`,
          { duration: 5000 }
        );
      }
      
      setTimeout(() => {
        navigate(`/login/${orgId}`);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || '註冊失敗，請稍後再試或聯繫管理員');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('${org?.background_image || 'https://images.unsplash.com/photo-1534258936925-c58bed479fcb?w=1200'}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/40 to-teal-500/40" />
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8">
          {org?.logo_url && (
            <div className="text-center mb-6">
              <img src={org.logo_url} alt="Logo" className="h-16 mx-auto" />
            </div>
          )}

          <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">註冊帳戶</h2>
          <p className="text-center text-gray-600 mb-6">{org?.name || '加入我們'}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 學生姓名 - 只對學生顯示 */}
            {formData.role === 'student' && (
              <div>
                <Input
                  type="text"
                  placeholder="學生姓名"
                  value={formData.student_name}
                  onChange={(e) => setFormData({...formData, student_name: e.target.value})}
                  className="h-11"
                />
              </div>
            )}

            <div>
              <Input
                data-testid="reg-username-input"
                type="text"
                placeholder={formData.role === 'student' ? "用戶名稱（顯示於排行榜）" : "用戶名稱"}
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="h-11"
              />
            </div>

            <div>
              <Input
                data-testid="reg-password-input"
                type="password"
                placeholder="密碼"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="h-11"
              />
            </div>

            <div>
              <Input
                data-testid="reg-email-input"
                type="email"
                placeholder="電郵"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="h-11"
              />
            </div>

            <div>
              <Input
                data-testid="reg-phone-input"
                type="tel"
                placeholder="電話"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="h-11"
              />
            </div>

            <div>
              <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                <SelectTrigger data-testid="role-select" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">學生</SelectItem>
                  <SelectItem value="parent">家長</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Gender selection for students */}
            {formData.role === 'student' && (
              <>
                <div>
                  <Select value={formData.gender} onValueChange={(value) => setFormData({...formData, gender: value})}>
                    <SelectTrigger data-testid="gender-select" className="h-11">
                      <SelectValue placeholder="選擇性別（可選）" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">男</SelectItem>
                      <SelectItem value="female">女</SelectItem>
                      <SelectItem value="other">其他</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Select value={formData.class_location} onValueChange={(value) => setFormData({...formData, class_location: value})}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="選擇上堂地點（可選）" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.length === 0 ? (
                        <SelectItem value="no_location" disabled>暫無地點</SelectItem>
                      ) : (
                        locations.map((location) => (
                          <SelectItem key={location.location_id} value={location.name}>
                            {location.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Show student ID field for parent registration */}
            {formData.role === 'parent' && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  學生ID（用於綁定）
                </label>
                <Input
                  data-testid="student-id-link-input"
                  type="text"
                  placeholder="輸入學生ID (例如: STU624101BA)"
                  value={formData.student_id_to_link}
                  onChange={(e) => setFormData({...formData, student_id_to_link: e.target.value})}
                  className="h-11"
                />
                <p className="text-xs text-gray-600 mt-1">
                  請輸入您孩子的學生ID以綁定帳戶
                </p>
              </div>
            )}

            <Button 
              data-testid="register-btn"
              type="submit" 
              className="w-full h-11"
              style={{ backgroundColor: org?.theme_color || '#FF6B35' }}
              disabled={loading}
            >
              {loading ? '註冊中...' : '註冊'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="back-to-login-btn"
              onClick={() => navigate(`/login/${orgId}`)}
              className="text-sm text-gray-700 hover:underline"
            >
              已有帳戶？登入
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
